import { GroupChannel } from '@sendbird/chat/groupChannel';
import { useMutation, type UseMutationOptions } from '@tanstack/react-query';
import { useNotify, useTranslate } from 'ra-core';
import { type SendableMessage } from '../../../resources/Chat/ForwardMessageProvider';
import { MessageType } from '@sendbird/chat/message';

type ForwardMessageParams = {
  message: SendableMessage;
  channels: GroupChannel[];
};

const forwardMessage = async ({ message, channels }: ForwardMessageParams) => {
  if (message.isFileMessage()) {
    const fetchRes = await fetch(message.url);
    if (!fetchRes.ok) throw new Error();

    const buffer = await fetchRes.arrayBuffer();
    const file = new File([buffer], message.name, { type: message.type });

    for (const channel of channels) {
      channel.sendFileMessage({
        file,
        fileName: message.name,
        mimeType: message.type,
      });
    }
  } else if (message.messageType === MessageType.USER) {
    for (const channel of channels) {
      channel.sendUserMessage({ message: message.message });
    }
  } else {
    throw new Error();
  }
};

export const useForwardMessage = (
  options?: UseMutationOptions<void, Error, ForwardMessageParams>
) => {
  const notify = useNotify();
  const translate = useTranslate();
  // eslint-disable-next-line @typescript-eslint/no-invalid-void-type
  return useMutation<void, Error, ForwardMessageParams>({
    mutationKey: ['forward-message'],
    mutationFn: forwardMessage,
    ...options,
    onError: () => {
      notify(translate('chat.somethingWentWrong'), {
        type: 'error',
        autoHideDuration: 5000,
      });
    },
    onSuccess: (data, variables, onMutateResult, context) => {
      void options?.onSuccess?.(data, variables, onMutateResult, context);
    },
  });
};
